/**
 * Tutorial Positioning - Smart tooltip placement logic
 * Calculates optimal tooltip position based on available viewport space
 * RTL-aware for Hebrew UI
 */

const TOOLTIP_MARGIN = 12 // Gap between element and tooltip
const VIEWPORT_PADDING = 16 // Min distance from viewport edges
const ARROW_SIZE = 8 // Size of tooltip arrow

/**
 * Get responsive tooltip max width based on viewport
 * @returns {number} Max tooltip width in pixels
 */
function getMaxTooltipWidth() {
  const viewportWidth = window.innerWidth
  if (viewportWidth < 640) return 280 // Mobile
  if (viewportWidth < 1024) return 360 // Tablet
  return 420 // Desktop
}

/**
 * Calculate available space in each direction from an element
 * @param {DOMRect} rect - Element bounding rect
 * @returns {Object} Available space in each direction
 */
function getAvailableSpace(rect) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return {
    top: rect.top - VIEWPORT_PADDING,
    bottom: viewportHeight - rect.bottom - VIEWPORT_PADDING,
    left: rect.left - VIEWPORT_PADDING,
    right: viewportWidth - rect.right - VIEWPORT_PADDING,
  }
}

/**
 * Determine the best position for the tooltip
 * Priority: bottom → top → start (right in RTL) → end (left in RTL)
 * @param {DOMRect} rect - Target element bounding rect
 * @param {number} tooltipHeight - Estimated tooltip height
 * @param {number} tooltipWidth - Tooltip width
 * @returns {'top' | 'bottom' | 'start' | 'end'} Best position
 */
function getBestPosition(rect, tooltipHeight, tooltipWidth) {
  const space = getAvailableSpace(rect)
  const minVerticalSpace = tooltipHeight + TOOLTIP_MARGIN + ARROW_SIZE
  const minHorizontalSpace = tooltipWidth + TOOLTIP_MARGIN + ARROW_SIZE

  // Prefer bottom, then top (most common tooltip positions)
  if (space.bottom >= minVerticalSpace) return 'bottom'
  if (space.top >= minVerticalSpace) return 'top'

  // For RTL: 'start' means right side, 'end' means left side
  if (space.right >= minHorizontalSpace) return 'start'
  if (space.left >= minHorizontalSpace) return 'end'

  // Fallback to bottom even if not ideal
  return 'bottom'
}

/**
 * Calculate tooltip position coordinates
 * @param {HTMLElement} targetElement - The highlighted element
 * @param {number} tooltipHeight - Actual tooltip height
 * @param {number} tooltipWidth - Actual tooltip width
 * @returns {Object} Position data with coordinates and placement
 */
export function calculateTooltipPosition(targetElement, tooltipHeight = 150, tooltipWidth = null) {
  if (!targetElement) {
    return { top: 0, left: 0, placement: 'bottom', arrowPosition: 50 }
  }

  const rect = targetElement.getBoundingClientRect()
  const maxWidth = getMaxTooltipWidth()
  const effectiveWidth = tooltipWidth || maxWidth

  const placement = getBestPosition(rect, tooltipHeight, effectiveWidth)
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let top = 0
  let left = 0
  let arrowPosition = 50 // Percentage for arrow placement

  switch (placement) {
    case 'bottom':
      top = rect.bottom + TOOLTIP_MARGIN + ARROW_SIZE
      left = rect.left + rect.width / 2 - effectiveWidth / 2
      break

    case 'top':
      top = rect.top - tooltipHeight - TOOLTIP_MARGIN - ARROW_SIZE
      left = rect.left + rect.width / 2 - effectiveWidth / 2
      break

    case 'start': // Right side in RTL
      top = rect.top + rect.height / 2 - tooltipHeight / 2
      left = rect.right + TOOLTIP_MARGIN + ARROW_SIZE
      break

    case 'end': // Left side in RTL
      top = rect.top + rect.height / 2 - tooltipHeight / 2
      left = rect.left - effectiveWidth - TOOLTIP_MARGIN - ARROW_SIZE
      break
  }

  // Clamp horizontal position to viewport
  if (left < VIEWPORT_PADDING) {
    const diff = VIEWPORT_PADDING - left
    arrowPosition = Math.max(10, 50 - (diff / effectiveWidth) * 100)
    left = VIEWPORT_PADDING
  } else if (left + effectiveWidth > viewportWidth - VIEWPORT_PADDING) {
    const diff = left + effectiveWidth - (viewportWidth - VIEWPORT_PADDING)
    arrowPosition = Math.min(90, 50 + (diff / effectiveWidth) * 100)
    left = viewportWidth - effectiveWidth - VIEWPORT_PADDING
  }

  // Clamp vertical position to viewport
  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING
  } else if (top + tooltipHeight > viewportHeight - VIEWPORT_PADDING) {
    top = viewportHeight - tooltipHeight - VIEWPORT_PADDING
  }

  return {
    top: Math.round(top),
    left: Math.round(left),
    placement,
    arrowPosition: Math.round(arrowPosition),
    maxWidth,
  }
}

/**
 * Calculate spotlight/cutout dimensions for the overlay
 * @param {HTMLElement} targetElement - The highlighted element
 * @param {number} padding - Extra padding around the element
 * @returns {Object} Spotlight dimensions
 */
export function calculateSpotlightRect(targetElement, padding = 8) {
  if (!targetElement) {
    return { top: 0, left: 0, width: 0, height: 0, borderRadius: 8 }
  }

  const rect = targetElement.getBoundingClientRect()
  const computedStyle = window.getComputedStyle(targetElement)
  const borderRadius = parseInt(computedStyle.borderRadius) || 8

  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    borderRadius: borderRadius + padding / 2,
  }
}
